import React from 'react';
import { getJsonValueAtPath } from '@backstage/filter-predicates';
import type {
  FieldExtensionComponentProps,
  FieldSchema,
} from '@backstage/plugin-scaffolder-react';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import {
  createFormField,
  type FormField,
} from '@backstage/plugin-scaffolder-react/alpha';

import { ChoiceField } from './choice-field';
import { choiceStateOf, sourceKeyOf } from './choice-state';
import { normalizeDependsOn } from './depends-on';
import { isJsonObject, isJsonValue, type JsonValue } from './json-value';
import { useDependsOn } from './use-depends-on';

function stringAtPath(item: JsonValue, path: string): string | undefined {
  const value = getJsonValueAtPath(item, path);
  return typeof value === 'string' ? value : undefined;
}

export interface FieldListPickerUiOptions {
  dependsOn: string | string[];
  /** Max 256 characters. Defaults to `'name'`. */
  labelPath?: string;
  /** Max 256 characters. */
  sourcePath: string;
}

export const fieldListPickerSchema: FieldSchema<
  unknown,
  FieldListPickerUiOptions
> = makeFieldSchema({
  output: z => z.unknown(),
  uiOptions: z =>
    z.object({
      dependsOn: z.union([z.string(), z.array(z.string())]),
      labelPath: z.string().max(256).optional(),
      sourcePath: z.string().max(256),
    }),
});

const DEFAULT_LABEL_PATH = 'name';

/**
 * An object item keys itself by `id` when it has a string one, otherwise by
 * its labelPath value (so single-field lists like `{ name: 'x' }` are still
 * selectable). A non-object item keys itself by its own string form.
 */
function buildGetKey(
  labelPath: string,
): (item: JsonValue) => string | undefined {
  return item => {
    if (isJsonObject(item)) {
      const id = item.id;
      if (typeof id === 'string') return id;
      return stringAtPath(item, labelPath);
    }
    return String(item);
  };
}

function buildGetLabel(
  labelPath: string,
): (item: JsonValue) => string | undefined {
  return item =>
    isJsonObject(item) ? stringAtPath(item, labelPath) : String(item);
}

export function FieldListPickerComponent(
  props: FieldExtensionComponentProps<unknown, FieldListPickerUiOptions>,
): React.ReactElement {
  const uiOptions = props.uiSchema['ui:options'];
  const dependsOn = uiOptions?.dependsOn;
  const sourcePath = uiOptions?.sourcePath ?? '';
  const labelPath = uiOptions?.labelPath ?? DEFAULT_LABEL_PATH;

  const state = useDependsOn(props, dependsOn);
  const getKey = buildGetKey(labelPath);
  const getLabel = buildGetLabel(labelPath);

  const firstDependency = normalizeDependsOn(dependsOn)[0];
  const sourceValue =
    state.kind === 'ready' && firstDependency !== undefined
      ? state.values[firstDependency]
      : undefined;
  const sourceValueJson = isJsonValue(sourceValue) ? sourceValue : undefined;
  const resolved = getJsonValueAtPath(sourceValueJson, sourcePath);
  const items = Array.isArray(resolved) ? resolved : [];

  const formDataJson = isJsonValue(props.formData) ? props.formData : undefined;
  const selectedKey =
    formDataJson === undefined ? null : getKey(formDataJson) ?? null;
  const selectedLabel =
    formDataJson === undefined ? null : getLabel(formDataJson) ?? null;

  return (
    <ChoiceField
      description={props.schema.description}
      emptyText={`No items found at ${sourcePath}`}
      getKey={getKey}
      getLabel={getLabel}
      items={items}
      label={props.schema.title ?? props.name}
      onSelect={item => props.onChange(item)}
      required={props.required}
      selectedKey={selectedKey}
      selectedLabel={selectedLabel}
      sourceKey={sourceKeyOf(state)}
      state={choiceStateOf(state)}
    />
  );
}

export const FieldListPicker: FormField = createFormField({
  component: FieldListPickerComponent,
  name: 'FieldListPicker',
  schema: fieldListPickerSchema,
});
